import { inject } from "@angular/core";
import { CanActivateFn, GuardResult, RedirectCommand, Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { AuthorizationService } from "../services/authorization.service";
import { RoleType } from "./../../api/client";

/**
 * Creates a route guard that checks if the current user has ALL specified roles.
 * If the user doesn't have all required roles, they will be redirected to the unauthorized page.
 *
 * @param role - The primary role to check
 * @param otherRoles - Additional roles that must also be present
 * @returns A CanActivateFn that resolves to either true or a redirect command
 *
 * @example
 * ```typescript
 * const routes: Routes = [{
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [roleGuard('ADMIN', 'SUPERVISOR')]
 * }];
 * ```
 */
export function roleGuard(role: RoleType, ...otherRoles: readonly RoleType[]): CanActivateFn {
  return async (): Promise<GuardResult> => {
    const authorization = inject(AuthorizationService);
    const router = inject(Router);
    if (await firstValueFrom(authorization.hasRoles(role, ...otherRoles))) {
      return true;
    }
    return new RedirectCommand(router.createUrlTree(["/unauthorized"]), { skipLocationChange: true });
  };
}

/**
 * Creates a route guard that checks if the current user has AT LEAST ONE of the specified roles.
 * If the user doesn't have any of the required roles, they will be redirected to the unauthorized page.
 *
 * @param role1 - First role to check
 * @param role2 - Second role to check
 * @param otherRoles - Additional roles to check
 * @returns A CanActivateFn that resolves to either true or a redirect command
 *
 * @example
 * ```typescript
 * const routes: Routes = [{
 *   path: 'dashboard',
 *   component: DashboardComponent,
 *   canActivate: [anyRoleGuard('USER', 'ADMIN', 'VIEWER')]
 * }];
 * ```
 */
export function anyRoleGuard(role1: RoleType, role2: RoleType, ...otherRoles: readonly RoleType[]): CanActivateFn {
  return async (): Promise<GuardResult> => {
    const authorization = inject(AuthorizationService);
    const router = inject(Router);
    if (await firstValueFrom(authorization.hasAnyRole(role1, role2, ...otherRoles))) {
      return true;
    }
    return new RedirectCommand(router.createUrlTree(["/unauthorized"]), { skipLocationChange: true });
  };
}
